import { PLATFORM_NAME, PRODUCTS } from '@app/shared/brand'
import { siteOrigin } from '../../lib/public-router'
import { SiteShell } from '../../ui/SiteShell'
import { WayIn } from '../../ui/WayIn'
import { Tile } from './Tile'
import './marketing.css'

/**
 * The platform's own front door.
 *
 * **It argues the idea, not a product.** Three claims hold across everything
 * here — it is built for the frum community, it is designed rather than
 * assembled, and an assistant does the work — over one way of working: the
 * facts and the rules are held as data, and what comes out of them is worked
 * out per occasion rather than authored.
 *
 * **Said without the vocabulary, and pinned to no use case.** "Dynamic data
 * plus presentation rules, resolved per occasion" is the true sentence and it
 * is unreadable to the person this page is for; naming a year and a printed
 * sheet is readable and sells the shul, which is one product's case rather than
 * the platform's. So the lede states both halves in plain words — what you know
 * is data, how it looks is a rule — and lists the shapes without ranking them.
 * It is equally true of a schedule, a sefer, a school and a chesed fund, which
 * is what makes it the platform's to say. A platform page arguing a product's case competes with the
 * page that exists to argue it, and whichever wins, one of them is now the
 * wrong place to change the copy.
 *
 * So the products are a LIST here and nothing more: a name, a line, a link to
 * the site that makes the case.
 *
 * It is short because it is finished, not because it is a placeholder: it grows
 * when there is something true to add, never because sections were reserved.
 */
export function PlatformHomePage({ onLogin }: { onLogin?: () => void }) {
  return (
    <SiteShell onLogin={onLogin}>
      <section className="mk-hero">
        <div className="mk-measure mk-hero__inner">
          <p className="mk-label">{PLATFORM_NAME}</p>
          <h1 className="mk-hero__title">
            Say it once. It takes every shape you need.
          </h1>
          <p className="mk-hero__lede">
            What you know is held as data and how it looks is a rule, so a page,
            a sheet, a book or a message is laid out rather than written again.
            Carefully designed, with an assistant that does the work.
          </p>
          <div className="mk-hero__actions">
            <WayIn onSignIn={onLogin} />
          </div>
        </div>
      </section>

      <section className="mk-section">
        <div className="mk-measure">
          <header className="mk-section__head">
            <p className="mk-eyebrow">Products</p>
            <h2 className="mk-section__title">
              One model, in as many shapes as it takes.
            </h2>
            <p className="mk-section__lede">
              Each product is its own thing with its own vocabulary, over one
              account, one tenancy and one design.
            </p>
          </header>
          <ul className="mk-tiles">
            {Object.values(PRODUCTS).map((p) => (
              <li key={p.slug}>
                {/* Each product's own site is where its case is made, so this
                    is a link to it rather than a summary of it. */}
                <Tile
                  href={siteOrigin(p.slug)}
                  name={p.name}
                  line={p.tagline}
                />
              </li>
            ))}
            {/* What is coming, named as nothing in particular. A tile with a
                product's name on it before that product exists is a promise
                somebody can hold us to; the honest version says only that the
                list is not finished. It leads nowhere, so it is not a control
                and does not pretend to be one. */}
            <li>
              <Tile
                quiet
                name="More on the way"
                line="Other institutions, other kinds of work — the same model underneath."
              />
            </li>
          </ul>
        </div>
      </section>
    </SiteShell>
  )
}
